import L from 'leaflet';
import type { Geometry, XZ } from '../geo/types';
import { latLngToXZ, toLatLng, xzToLatLng } from '../geo/crs';
import { round2 } from '../geo/format';
import { typeDef } from '../featureTypes/registry';
import { ICONS } from '../icons/tabler';
import type { FeatureRecord, Status } from '../lib/types';

// ── feature → Leaflet ─────────────────────────────────────────────────────────────────────

const STATUS_DASH: Record<Status, string | undefined> = {
  idea: '3 6',
  proposed: '9 6',
  approved: undefined,
  in_unity: undefined,
  needs_update: undefined,
  deprecated: '2 7',
};

export function featureColor(f: Pick<FeatureRecord, 'type' | 'color'>): string {
  return f.color || typeDef(f.type).color;
}

export function pathStyle(f: FeatureRecord, selected: boolean): L.PathOptions {
  const color = featureColor(f);
  const isLine = f.geometry.type === 'polyline';
  const style: L.PathOptions = {
    color,
    weight: f.status === 'in_unity' ? 2.5 : 2,
    opacity: 0.95,
    fillColor: color,
    fillOpacity: f.type === 'zone' ? 0.14 : 0.2,
    dashArray: STATUS_DASH[f.status],
    fill: !isLine,
  };
  if (f.status === 'idea') style.fillOpacity = 0.07;
  if (f.status === 'needs_update') style.color = '#ff8f00';
  if (f.status === 'deprecated') {
    style.color = '#8a8a8a';
    style.fillColor = '#8a8a8a';
    style.opacity = 0.6;
  }
  if (f.type === 'path') style.weight = 3;
  if (selected) {
    style.weight = (style.weight ?? 2) + 2;
    style.fillOpacity = (style.fillOpacity ?? 0.1) + 0.12;
  }
  return style;
}

export function iconSvg(iconId: string, size = 16): string {
  const inner = ICONS[iconId] ?? ICONS.poi;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}

export function markerIcon(f: FeatureRecord, selected: boolean, stepNo?: number): L.DivIcon {
  const def = typeDef(f.type);
  const iconId = (
    typeof f.properties.icon === 'string' && ICONS[f.properties.icon] ? f.properties.icon : def.icon
  ) as string;
  const inner = stepNo !== undefined ? `<span class="fm-num">${stepNo}</span>` : iconSvg(iconId, 15);
  const badge =
    f.status === 'in_unity'
      ? '<span class="fm-badge ok"></span>'
      : f.status === 'needs_update'
        ? '<span class="fm-badge warn"></span>'
        : '';
  return L.divIcon({
    className: 'fm-wrap',
    html: `<div class="fm fm-${f.status}${selected ? ' fm-sel' : ''}" style="--c:${featureColor(f)}">${inner}${badge}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    tooltipAnchor: [12, 0],
  });
}

/** Builds the Leaflet layer for a feature's geometry (null for `none`). */
export function buildLayer(
  f: FeatureRecord,
  selected: boolean,
  pane: string,
  stepNo?: number,
): L.Layer | null {
  const g = f.geometry;
  const common = { pane, pmIgnore: false } as L.PathOptions;
  switch (g.type) {
    case 'point':
      return L.marker(toLatLng(g.x, g.z), {
        icon: markerIcon(f, selected, stepNo),
        pane: 'markers',
        riseOnHover: true,
        keyboard: false,
      });
    case 'circle':
      return L.circle(toLatLng(g.x, g.z), { ...common, ...pathStyle(f, selected), radius: g.r });
    case 'rect':
      return L.rectangle(
        L.latLngBounds(toLatLng(g.x - g.w / 2, g.z - g.d / 2), toLatLng(g.x + g.w / 2, g.z + g.d / 2)),
        { ...common, ...pathStyle(f, selected) },
      );
    case 'polygon':
      return L.polygon(g.points.map(xzToLatLng), { ...common, ...pathStyle(f, selected) });
    case 'polyline':
      return L.polyline(g.points.map(xzToLatLng), { ...common, ...pathStyle(f, selected) });
    default:
      return null;
  }
}

// ── Leaflet → geometry (after drawing / editing) ─────────────────────────────────────────

function ringToXZ(latlngs: L.LatLng[] | L.LatLng[][]): XZ[] {
  const ring = (Array.isArray(latlngs[0]) ? latlngs[0] : latlngs) as L.LatLng[];
  return ring.map((ll) => {
    const [x, z] = latLngToXZ(ll);
    return [round2(x), round2(z)] as XZ;
  });
}

/** `prev` keeps point y/rotY that the 2D editor can't change. */
export function geometryFromLayer(layer: L.Layer, prev?: Geometry): Geometry | null {
  if (layer instanceof L.Marker) {
    const ll = layer.getLatLng();
    const out: Geometry = { type: 'point', x: round2(ll.lng), z: round2(ll.lat) };
    if (prev?.type === 'point' && prev.rotY != null) out.rotY = prev.rotY;
    return out;
  }
  if (layer instanceof L.Circle) {
    const ll = layer.getLatLng();
    return { type: 'circle', x: round2(ll.lng), z: round2(ll.lat), r: round2(layer.getRadius()) };
  }
  if (layer instanceof L.Rectangle) {
    const b = layer.getBounds();
    return {
      type: 'rect',
      x: round2((b.getWest() + b.getEast()) / 2),
      z: round2((b.getSouth() + b.getNorth()) / 2),
      w: round2(b.getEast() - b.getWest()),
      d: round2(b.getNorth() - b.getSouth()),
    };
  }
  if (layer instanceof L.Polygon) {
    const points = ringToXZ(layer.getLatLngs() as L.LatLng[][]);
    return points.length >= 3 ? { type: 'polygon', points } : null;
  }
  if (layer instanceof L.Polyline) {
    const points = ringToXZ(layer.getLatLngs() as L.LatLng[]);
    return points.length >= 2 ? { type: 'polyline', points } : null;
  }
  return null;
}

/** Geoman draw shape for a geometry kind. */
export function geomanShape(kind: Geometry['type']): string | null {
  switch (kind) {
    case 'point':
      return 'Marker';
    case 'circle':
      return 'Circle';
    case 'rect':
      return 'Rectangle';
    case 'polygon':
      return 'Polygon';
    case 'polyline':
      return 'Line';
    default:
      return null;
  }
}
